import { action, computed, makeObservable } from 'mobx';

import type { OthersEntity } from './Others.entity';
import type { Stack } from '../../../../packages/data/structures/Stack';
import type { BaseSettingsScreen } from '../screen/BaseSettingsScreen';
import type {
  LocationNameConfig,
  LocationNameConfigurator,
  LocationNameDisplayType,
} from 'src/packages/user-preference/UserPreference.api';

import { requireService } from '../../../../packages/di';

export class OthersScreenStore {
  readonly stack: Stack<BaseSettingsScreen>;
  readonly screenEntity: OthersEntity;

  constructor(
    stack: Stack<BaseSettingsScreen>,
    entity: OthersEntity,
    private readonly otherSettings = requireService('otherSettings'),
    private readonly userPreference = requireService('userPreference')
  ) {
    this.stack = stack;
    this.screenEntity = entity;
    makeObservable(this);
  }

  @computed
  get isAltNames() {
    return this.userPreference.preferences.isAltNames ?? true;
  }

  @action.bound
  setIsAltNamesOn(value: boolean) {
    this.userPreference.updatePreferences({ isAltNames: value });
  }

  @computed
  get isColoredParamsHeaders() {
    return this.userPreference.preferences.isColoredParamsHeaders ?? false;
  }

  @action.bound
  setIsColoredParamsHeaders(value: boolean) {
    this.userPreference.updatePreferences({ isColoredParamsHeaders: value });
  }

  @computed
  get widgetHeaderFontSize() {
    return this.otherSettings.widgetHeaderFontSize;
  }

  @action.bound
  setWidgetHeaderFontSize(value: number) {
    this.otherSettings.selectWidgetHeaderFontSize(value);
  }

  @computed
  get graphicsPerformance() {
    return this.otherSettings.graphicsPerformance;
  }

  @action.bound
  setGraphicsPerformance(value: 'ultra' | 'high' | 'medium' | 'low') {
    this.otherSettings.selectGraphicsPerformance(value);
  }

  @computed
  get defaultDepthUnits() {
    return this.userPreference.preferences.defaultDepthUnits ?? 'INDEX_DEPTH_SCALE_METERS';
  }

  @action.bound
  setDefaultDepthUnits(value: 'INDEX_DEPTH_SCALE_METERS' | 'INDEX_DEPTH_SCALE_FEET') {
    this.userPreference.updatePreferences({ defaultDepthUnits: value });
  }

  @computed
  get autoSelectWellboreForTime() {
    return this.userPreference.preferences.autoSelectWellboreForTime;
  }

  @action.bound
  setAutoSelectWellbore(value: 'active' | 'main') {
    this.userPreference.updatePreferences({ autoSelectWellboreForTime: value });
  }

  @computed
  get autoSelectWellboreForDepth() {
    return this.userPreference.preferences.autoSelectWellboreForDepth;
  }

  @action.bound
  setAutoSelectWellboreForDepth(value: 'active' | 'main') {
    this.userPreference.updatePreferences({ autoSelectWellboreForDepth: value });
  }

  @computed
  get defaultWellListFilter() {
    return this.userPreference.preferences.defaultWellListFilter ?? 'active';
  }

  @action.bound
  setDefaultWellListFilter(value: 'active' | 'favorites' | 'all') {
    this.userPreference.updatePreferences({ defaultWellListFilter: value });
  }

  @computed
  get defaultWellListGroup() {
    return this.userPreference.preferences.defaultWellListGroup ?? 'wellsGroupingLocation';
  }

  @action.bound
  setDefaultWellListGroup(
    value: 'wellsGroupingStatus' | 'wellsGroupingDrillingRig' | 'wellsGroupingRigCrew' | 'wellsGroupingLocation'
  ) {
    this.userPreference.updatePreferences({ defaultWellListGroup: value });
  }

  get hasAsyncLoading() {
    return this.userPreference.isLoading;
  }

  @computed
  get locationNameConfigurator() {
    return this.userPreference.preferences.locationNameConfigurator;
  }

  @action.bound
  setLocationNameConfig(locationType: keyof LocationNameConfigurator, config: LocationNameConfig) {
    this.userPreference.updatePreferences({
      locationNameConfigurator: { ...this.locationNameConfigurator, [locationType]: config },
    });
  }

  @action.bound
  setLocationDisplayName(locationType: keyof LocationNameConfigurator, displayName: LocationNameDisplayType) {
    const currentConfig = this.locationNameConfigurator[locationType];
    this.setLocationNameConfig(locationType, { ...currentConfig, displayName });
  }

  @action.bound
  setLocationOrder(locationType: keyof LocationNameConfigurator, order: number | null) {
    const currentConfig = this.locationNameConfigurator[locationType];
    this.setLocationNameConfig(locationType, { ...currentConfig, order });
  }

  @computed
  get defaultSplitRangeTime() {
    return this.userPreference.preferences.defaultSplitRange.time;
  }

  @computed
  get defaultSplitRangeDepth() {
    return this.userPreference.preferences.defaultSplitRange.depth;
  }

  @action.bound
  setDefaultSplitRangeTime(value: number) {
    this.userPreference.updatePreferences({ defaultSplitRange: { time: value, depth: this.defaultSplitRangeDepth } });
  }

  @action.bound
  setDefaultSplitRangeDepth(value: number) {
    this.userPreference.updatePreferences({ defaultSplitRange: { time: this.defaultSplitRangeTime, depth: value } });
  }
}
